import { Constants } from "@/api/constants";
import { Track } from "@/classes/Track";
import type { AppContext } from "@/types/scheduling";
import { businessIDs, isBusiness } from "@/utils/businessLeads";
import { clientRedirect, mapPath } from "@/utils/functions";
import { appendCreateLS } from "@/utils/localStorage";
//TODO replace parsePhoneNumber - it's deprecated
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { insuranceList } from "shared/constants";
import type { AppointmentData, InsuranceValue, LeadSelfServe, PaymentType, SelfServeInfoData } from "shared/types";
import { getCustomizationResponse } from "@/utils/customizeDietitiansCV";

export const handleOnSubmit = <T extends AppContext>(context: T) => {
  const constants = new Constants();
  const healthieSlotIds = constants.getStageConst("healthieSlotIds");

  let appointment: AppointmentData = {
    nutrition_area: context.result.nutrition_area.value,
    state: context.result.state.value,
    communication_style: context.result.communication_style.value,
    day: context.result.day.value,
    date_time: context.result.date_time,
    provider_id: context.result.provider_id,
    plan_id: context.plan_id,
    contact_type: "Healthie Video Call",
    timezone: context.result.timezone,
    appt_type_id: healthieSlotIds.apptTypeId,
    flow: context.flow,
    insurance_provider: context.result.insurance_provider?.value as InsuranceValue,
    insurance_provider_other: context.result.insurance_provider_other,
    lead_name: context.result.lead_name,
    lead_email: context.result.lead_email,
    lead_phone_number: parsePhoneNumberWithError(context.result.lead_phone_number, "US").number,
    payment_method: context.result.payment_method.value as PaymentType,
    business_name: context.result.business_name,
    is_artificial: context.result.is_artificial,
  };

  //TODO: appointment IDs should be part of the app's context
  const appointmentTypeIds = constants.getStageConst("appointmentTypeIds");
  if (appointment.payment_method === "insurance") {
    if (isBusiness(businessIDs.bold) || isBusiness(businessIDs.liveItUp)) {
      appointment = {
        ...appointment,
        appt_type_id: appointmentTypeIds["initial_consultation_insurance_30m"],
      };
    } else {
      appointment = {
        ...appointment,
        appt_type_id: appointmentTypeIds["initial_consultation_insurance"],
      };
    }
  }
  console.debug("onSubmit", appointment, context);

  if (!context.provider_data) throw new Error("RDE data is missing");
  const ssToSave: SelfServeInfoData = {
    type: "client",
    appointmentData: appointment,
    rds: context.provider_data,
  };
  appendCreateLS(ssToSave, "self_serve_data");
  const payload: LeadSelfServe = {
    stage: "appointment_selection",
    lead_rd_id: appointment.provider_id ?? undefined,
    lead_state: appointment.state,
    lead_nutrition_area: appointment.nutrition_area,
    lead_appointment_timedate: appointment.date_time,
    lead_name: appointment.lead_name,
    lead_email: appointment.lead_email,
    lead_phone_number: appointment.lead_phone_number,
    lead_insurance_provider:
      appointment.insurance_provider !== undefined
        ? insuranceList.find((i) => i.value === appointment?.insurance_provider)?.label
        : undefined,
    lead_insurance_provider_other: appointment.insurance_provider_other,
    lead_timezone: appointment.timezone,
    lead_payment_method: appointment.payment_method,
    lead_flow: appointment.flow,
    appointment_is_artificial: appointment.is_artificial,
    lead_business_name: appointment?.business_name,
  };
  if (appointment.provider_id) {
    const customRdProfile = getCustomizationResponse(appointment.provider_id);
    if (customRdProfile) {
      payload.lead_rd_custom_cv = customRdProfile.customCV;
    }
  }

  const track: Track = new Track();
  track.trackLead("selfServeAppointmentSelected", payload);

  const gtmEvent = {
    event: "appointment_selected",
    properties: payload,
  };
  window.dataLayer.push(gtmEvent);
  const onKlaviyoTrack = () => {
    if (window.klaviyo) {
      window.klaviyo.push(["track", "appointment_selected", payload]);
      console.debug("klaviyo appointment_selected track ran");
    }
  };

  if (appointment.lead_email) {
    if (window.klaviyo) {
      window.klaviyo.push([
        "identify",
        {
          email: appointment.lead_email,
          ...payload,
        },
        onKlaviyoTrack,
      ]);
      console.debug("klaviyo identify ran");
    }
  }
  let redirectURl = "";
  let timeout = 3500;
  if (
    appointment.flow.name === "quiz_flow" &&
    appointment.flow.id.includes("Quiz D2C Flow (Live Data)") &&
    appointment.payment_method === "insurance"
  ) {
    redirectURl = `/appointment/checkout?rd=${appointment.provider_id}&plan=insurance-session-60m`;
  } else if (
    appointment.flow.name === "quiz_flow" &&
    appointment.flow.id.includes("Quiz D2C Flow (Live Data)") &&
    appointment.payment_method !== "insurance"
  ) {
    redirectURl = `/appointment/checkout?rd=${appointment.provider_id}&plan=weekly`;
    redirectURl = `/appointment/checkout?rd=${appointment.provider_id}&plan=weekly`;
  } else {
    if (appointment.payment_method === "insurance" && isBusiness(businessIDs.bold)) {
      //TODO insurance-session comes from src/utils/constants.ts pricingOptions ID 6
      redirectURl = `/self-serve-checkout-v2?rd=${appointment.provider_id}&plan=insurance-session-30m`;
    } else if (appointment.payment_method === "insurance" && isBusiness(businessIDs.liveItUp)) {
      //TODO insurance-session comes from src/utils/constants.ts pricingOptions ID 6
      redirectURl = `/self-serve-checkout-v2?rd=${appointment.provider_id}&plan=insurance-session-30m`;
    } else if (appointment.payment_method === "insurance" && isBusiness(businessIDs.bma)) {
      redirectURl = `/self-serve-checkout-v2?rd=${appointment.provider_id}&plan=insurance-session-60m`;
    } else if (appointment.payment_method === "insurance" && isBusiness(businessIDs.sworn)) {
      redirectURl = `/self-serve-checkout-v2?rd=${appointment.provider_id}&plan=insurance-session-60m`;
    } else if (
      appointment.payment_method === "insurance" &&
      appointment.flow.name === "quiz_flow" &&
      (appointment.flow.id.includes("Quiz - Insurance (Improved Flow)") ||
        appointment.flow.id.includes("Quiz - Insurance (Improved Flow - Meta)") ||
        appointment.flow.id.includes("Quiz - Insurance (Improved Flow - Organic)")||
        appointment.flow.id.includes("Quiz - Insurance (Improved Flow - META 2)")||
        appointment.flow.id.includes("Quiz - Insurance (Improved Flow - Paid Search)")
    )) {
      // caspian insurance
      redirectURl =
        `/self-serve-loading?redirect=` +
        encodeURIComponent(mapPath(`/self-serve-checkout-v2?rd=${appointment.provider_id}&plan=insurance-session-60m`));
      timeout = 0;
    } else {
      redirectURl = `/self-serve-plan?rd=${appointment.provider_id}`;
    }
  }
  setTimeout(() => {
    clientRedirect(redirectURl);
  }, timeout);
};
